import { jsx as _jsx } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { TextLink, notConfiguredText } from '@wit-support/ui';
import { ContentEngineHealthDot, ModuleScreen } from '@wit-support/ui-domain';
import { ContentRequestError, getContentHealth, getContentOverview, getPublishSettings, } from './api.js';
/** Корень всех ключей раздела — одна инвалидация после мутации обновляет связанные экраны. */
export const CONTENT_KEY = ['content'];
export const HEALTH_KEY = ['content', 'health'];
export const OVERVIEW_KEY = ['content', 'overview'];
export const CAPTIONS_KEY = ['content', 'captions'];
const ENGINE_SERVICE = 'Контент-движок';
const ENGINE_ENV = ['CONTENT_ENGINE_API_BASE_URL', 'CONTENT_ENGINE_API_TOKEN'];
export const NOT_CONFIGURED_TEXT = notConfiguredText(ENGINE_SERVICE, ENGINE_ENV);
export const UNAVAILABLE_TEXT = 'Контент-движок недоступен, попробуйте позже';
export const FORBIDDEN_READ_TEXT = 'Раздел «Контент» недоступен вашей роли: его видят участники команды, супервайзеры и владелец.';
export const FORBIDDEN_WRITE_TEXT = 'Менять настройки контент-движка могут только супервайзер и владелец.';
/** `400 until_in_past` admin-api: срок отдыха клипа не в будущем по часам сервера. */
export const UNTIL_IN_PAST_TEXT = 'Дата окончания отдыха уже прошла';
/**
 * Ошибку — словами. `mode: 'write'` — для мутаций: 403 там значит «смотреть
 * можно, менять нельзя», а не «нет доступа к разделу».
 */
export function describeContentError(err, mode = 'read') {
    if (err instanceof ContentRequestError) {
        if (err.status === 403)
            return mode === 'write' ? FORBIDDEN_WRITE_TEXT : FORBIDDEN_READ_TEXT;
        if (err.code === 'content_engine_not_configured')
            return NOT_CONFIGURED_TEXT;
        if (err.code === 'until_in_past')
            return UNTIL_IN_PAST_TEXT;
        if (err.status === 502 || err.status === 503 || err.status === 504)
            return UNAVAILABLE_TEXT;
        return mode === 'write'
            ? `Не удалось сохранить: ${err.message}`
            : `Не удалось загрузить: ${err.message}`;
    }
    const message = err instanceof Error ? err.message : String(err);
    return mode === 'write' ? `Не удалось сохранить: ${message}` : `Не удалось загрузить: ${message}`;
}
/**
 * Сабмит диалога через `mutateAsync`: `ZodForm` ждёт промис, поэтому
 * `SubmitButton` показывает «Сохраняем…» и заблокирована — второй клик не
 * шлёт второй запрос. Ошибку уже показал `onError` мутации (toast), здесь она
 * гасится, иначе `ZodForm` пробросил бы её необработанной.
 */
export async function submitting(p) {
    await p.catch(() => undefined);
}
export function useContentHealth() {
    return useQuery({ queryKey: HEALTH_KEY, queryFn: getContentHealth });
}
/**
 * Аккаунты движка. Главный источник — конфиг расписания (`/publish-settings`:
 * там КАЖДЫЙ аккаунт, даже без единой публикации); к нему добавляются
 * аккаунты из обзора (публиковались, но из конфига уже убраны — история
 * остаётся видимой). Сбой любого источника — ошибка, а не пустой список:
 * иначе 502 выглядел бы как «аккаунтов пока нет».
 */
export function useContentAccounts() {
    const settings = useQuery({ queryKey: CAPTIONS_KEY, queryFn: getPublishSettings });
    const overview = useQuery({ queryKey: OVERVIEW_KEY, queryFn: getContentOverview });
    const accounts = [
        ...new Set([
            ...(settings.data?.items ?? []).map((a) => a.account),
            ...(overview.data?.tiles ?? []).map((t) => t.account),
        ]),
    ].sort((a, b) => a.localeCompare(b));
    return {
        accounts,
        isLoading: settings.isLoading || overview.isLoading,
        error: settings.error ?? overview.error ?? null,
    };
}
/**
 * Ссылка на пост в соцсети (новая вкладка); null — движок ещё не связал
 * публикацию с постом. `context` («аккаунт · сеть») — в доступное имя: в
 * таблице таких «Открыть» по одному на строку, и без контекста скринридер
 * зачитывает список одинаковых ссылок.
 */
/**
 * Имя клипа — ссылка в его карточку `/content/clips/<id>`. Одна на модуль:
 * публикации, карточка публикации (клипы и кандидаты), журнал решений, обзор,
 * клипы, двойник.
 */
export function ClipLink({ id, children }) {
    return (_jsx(TextLink, { asChild: true, children: _jsx(Link, { to: "/content/clips/$id", params: { id }, children: children }) }));
}
export function PostLink({ url, context }) {
    if (url === null)
        return _jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u2014" });
    return (_jsx(TextLink, { href: url, external: true, "aria-label": `Открыть пост: ${context}`, children: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C" }));
}
/**
 * Каркас экрана раздела (`ModuleScreen` из ui-domain): заголовок + проверка
 * «движок подключён и отвечает». Тело монтируется ТОЛЬКО когда `/health`
 * ответил `configured: true`. Точка здоровья движка — на каждом экране
 * раздела (§4 «Health dot»).
 */
export function ContentScreen({ title, description, actions, children }) {
    const health = useContentHealth();
    return (_jsx(ModuleScreen, { title: title, description: description, actions: actions, headerExtra: _jsx(ContentEngineHealthDot, { health: health.data }), health: health, service: ENGINE_SERVICE, envVars: ENGINE_ENV, describeError: describeContentError, unavailableText: UNAVAILABLE_TEXT, children: children }));
}
//# sourceMappingURL=shared.js.map